import { inject, Injectable, signal, TemplateRef } from '@angular/core'
import { BehaviorSubject, EMPTY, Observable, of, Subject, throwError } from 'rxjs'
import { tap, catchError, map, switchMap } from 'rxjs/operators'
import { PriceChangeType, PricingRowVM } from './vetcor-pricing-row.model'
import {
   ChangeType,
   HospitalPrice,
   NewCodeRequest,
   PracticeBatchInfo,
   PricingChangeRequest,
   PricingCycleUpdate,
   PricingManagerClient,
} from 'src/app/shared/services/ppv.api'
import { ToastrService } from 'ngx-toastr'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { VetcorPricingDetailsModal } from './vetcor-pricing-details-modal/vetcor-pricing-details-modal.component'
import { HospitalPricingService } from '../hospital-pricing.service'

@Injectable({ providedIn: 'root' })
export class VetcorPricingRowService {
   hospitalPricingService = inject(HospitalPricingService)
   prices = signal<HospitalPrice[]>(undefined)

   topLevelRows = signal<PricingRowVM[]>(undefined)

   selectedGroup = signal<PricingRowVM>(undefined)
   selectedPrice = signal<PricingRowVM>(undefined)

   priceChangeData = signal<any[]>([])
   detailButtonClicked = signal<boolean>(false)
   chartOptionsTwo = signal<any>(null)
   isEditing = signal<boolean>(false)
   recOverrideReason = signal<string>('')
   showValidationModal = signal<boolean>(false)
   validationMessage = signal<string>('')
   pricingUpdates = signal<any[] | null>(null)
   pricingUpdatesScheduled = signal<{ products: any[]; data: any[] } | null>(null)
   pricingUpdatesKey = signal<number>(-1)
   pricingUpdatesScheduledKey = signal<number>(-1)
   selectedChartYear: number = new Date().getFullYear()

   constructor(private pricingClient: PricingManagerClient, private toastr: ToastrService) {}

   setPrices(prices: HospitalPrice[]) {
      this.prices.set(prices)
      this.setupRowVMs()
   }

   _getPriceGroup(product: PricingRowVM): PricingRowVM {
      if (product.parentId) return this.topLevelRows().find((r) => r.id === product.parentId)

      return product
   }

   _isSubRow(price: HospitalPrice) {
      return price.recordType == 'PF' || price.recordType == 'MP' || price.recordType == 'PB' || price.recordType == 'PG'
   }

   _getRowVm(price: HospitalPrice): PricingRowVM {
      const vm: PricingRowVM = {
         id: `${price.pimsId}-${price.recordType}`,
         pimsId: price.pimsId,
         practiceSKey: price.practiceSKey,
         code: price.code,
         canUpdatePrice: price.canUpdatePrice,
         recordType: price.recordType,
         recordName: price.recordName,
         isExpanded: false,
         pimsCategory: price.pimsCategory,
         l12MRevenue: price.metrics?.l12MRevenueContribution,
         pctAnnualRevenue: price.metrics?.l12MRevenueContributionPct,
         l12MTransactionPct: price.metrics?.l12MTransactionPct,
         distinctClientCount: price.metrics?.l12MDistinctClient,
         currentPrice: price.price,
         modelPrice: price.cycleUpdate?.newPrice,
         subRows: [],
         isService: price.productTypeSKey == 1 ? false : true,
         approvalStatus: undefined,
         changeType: undefined,
         parentId: undefined,
         metrics: price.metrics,
         practiceName: price.practiceName,
         siteName: price.siteName,
      }

      // Child rows
      if (!!price.recordType && price.recordType != 'P' && price.recordType != 'SF') {
         vm.parentId = `${price.pimsId}-P`
      }

      vm.cycleUpdateId = price.cycleUpdate?.id

      // If we get too many of these, go with the factory/interface pattern for change types/requests
      if (!!price.newCodeRequest && !!price.newCodeRequestId) {
         vm.changeType = 'New'
         vm.requestedOn = price.newCodeRequest.requestedOn
         vm.opsPrice = price.newCodeRequest.productPrice
         vm.finalComment = price.newCodeRequest.finalComment
         vm.newCodeRequestId = price.newCodeRequestId
         vm.zoho = !!price.newCodeRequest.zohoTicketId
         vm.zohoTicketId = price.newCodeRequest.zohoTicketId
         vm.approvalStatus = price.newCodeRequest.isApproved ? 'Approved' : price.newCodeRequest.isApproved === false ? 'Rejected' : 'Pending'
      }

      if (!!price.cycleUpdate) {
         vm.changeType = 'Cycle'
         vm.modelPrice = price.cycleUpdate.newPrice
         vm.newPrice = price.cycleUpdate.reviewPrice
         vm.opsPrice = price.cycleUpdate.opsPrice
         vm.finalComment = price.cycleUpdate.finalComment
         vm.rationale = price.cycleUpdate.reviewComment
         vm.opsPriceRationale = price.cycleUpdate.finalComment
         vm.status = price.cycleUpdate.status
         vm.approvalStatus = price.cycleUpdate.captainApproved == 1 ? 'Approved' : price.cycleUpdate.captainApproved == 2 ? 'Rejected' : 'Pending'
      } else if (!!price.pricingChangeRequest) {
         vm.changeType = 'Practice'
         vm.requestedOn = price.pricingChangeRequest.requestedOn
         vm.newPrice = price.pricingChangeRequest.reviewPrice
         vm.rationale = price.pricingChangeRequest.reviewComment
         vm.opsPrice = price.pricingChangeRequest.opsPrice
         vm.opsPriceRationale = price.pricingChangeRequest.finalComment
         vm.pricingChangeRequestId = price.pricingChangeRequest.id
         // We need to send down the corresponding writeback status I think for all of these
         // vm.status
         vm.zoho = !!price.pricingChangeRequest.zohoTicketId
         vm.zohoTicketId = price.pricingChangeRequest.zohoTicketId

         if (price.pricingChangeRequest.captainApproved == 2) vm.approvalStatus = 'Rejected'
         else if (!!price.pricingChangeRequest.zohoTicketId && !price.pricingChangeRequest.captainApproved) vm.approvalStatus = 'Discussion'
         else if (
            price.pricingChangeRequest.captainApproved == 1 &&
            price.pricingChangeRequest.opsPrice != price.pricingChangeRequest.reviewPrice &&
            !price.newCodeRequest
         )
            vm.approvalStatus = 'Overridden'
         else if (price.pricingChangeRequest.captainApproved == 1) {
            vm.approvalStatus = 'Approved'
         } else {
            vm.approvalStatus = 'Pending'
         }
      }

      vm.priceImpact = price.cycleUpdate?.revenueImpact

      return vm
   }

   _applyVMPriceCalculations(pricingRow: PricingRowVM) {
      pricingRow.totalFees = pricingRow.subRows.filter((sr) => sr.recordType == 'PF').reduce((sum, fee) => sum + (fee.currentPrice || 0), 0)

      pricingRow.opsFees = pricingRow.subRows
         .filter((sr) => sr.recordType == 'PF')
         .reduce((sum, fee) => sum + (fee.opsPrice || fee.modelPrice || fee.currentPrice || 0), 0)

      pricingRow.totalRequestedPrice = (pricingRow.newPrice || pricingRow.modelPrice || pricingRow.currentPrice) + pricingRow.requestedFees

      pricingRow.requestedFees = pricingRow.subRows
         .filter((sr) => sr.recordType == 'PF')
         .reduce((sum, fee) => sum + (fee.newPrice || fee.modelPrice || fee.currentPrice || 0), 0)

      pricingRow.totalRequestedPrice = (pricingRow.newPrice || pricingRow.modelPrice || pricingRow.currentPrice) + pricingRow.requestedFees
      pricingRow.totalCurrentPrice = pricingRow.totalFees + pricingRow.currentPrice

      pricingRow.totalOpsPrice = (pricingRow.opsPrice || pricingRow.modelPrice || pricingRow.currentPrice) + pricingRow.opsFees
   }

   setupRowVMs() {
      const groupedMap = new Map<string, PricingRowVM>()
      const priceVMRows: PricingRowVM[] = []
      this.prices()
         ?.filter((p) => !this._isSubRow(p))
         .forEach((p) => {
            const vm = this._getRowVm(p)
            priceVMRows.push(vm)

            const id = p.productSKey
               ? p.productSKey.toString()
               : p.newCodeRequest?.id
               ? `NEW-${p.newCodeRequest?.id}`
               : p.recordType == 'SF'
               ? `SF-${p.productSKey}`
               : undefined

            if (!id) {
               throw 'Unknown id'
            }

            groupedMap.set(id, vm)
         })

      this.prices()
         .filter((p) => this._isSubRow(p))
         .forEach((p) => {
            const parentProduct = groupedMap.get(p.productSKey.toString())
            const vm = this._getRowVm(p)
            parentProduct?.subRows.push(vm)
         })

      priceVMRows.forEach((p) => this._applyVMPriceCalculations(p))
      this.topLevelRows.set(priceVMRows)
   }

   undoPracticePricingChangeRequest(product: PricingRowVM): Observable<any> {
      this.updateRowVMs([{ ...product, isSaving: true }])

      const apiCall = this.pricingClient.addOrUpdatePricingChangeRequest({
         id: product.pricingChangeRequestId,
         reviewPrice: null,
         reviewComment: '',
      } as PricingChangeRequest)

      return apiCall.pipe(
         tap(() => {
            this.updateRowVMs([{ ...product, newPrice: undefined, rationale: undefined, pricingChangeRequestId: null, isSaving: false }])
         }),
         catchError((error) => {
            product.isSaving = false
            if (error?.response?.indexOf('"error":"RulesEngineValidation"') === -1) {
               this.toastr.error('Server communication error. Please contact support!')
            }
            return throwError(() => error)
         })
      )
   }

   undoCaptainPricingChangeRequest(product: PricingRowVM): Observable<any> {
      this.updateRowVMs([{ ...product, isSaving: true }])

      const apiCall = this.pricingClient.addOrUpdatePricingChangeRequest({
         id: product.pricingChangeRequestId,
         reviewPrice: null,
         reviewComment: '',
      } as PricingChangeRequest)

      return apiCall.pipe(
         tap(() => {
            this.updateRowVMs([
               {
                  ...product,
                  opsPrice: undefined,
                  finalComment: undefined,
                  approvalStatus: 'Pending',
                  isSaving: false,
                  zohoTicketId: undefined,
                  zoho: false,
                  // actionedOn: null,
                  // notificationSent: null,
               },
            ])
         }),
         catchError((error) => {
            product.isSaving = false
            if (error?.response?.indexOf('"error":"RulesEngineValidation"') === -1) {
               this.toastr.error('Server communication error. Please contact support!')
            }
            return throwError(() => error)
         })
      )
   }

   overridePricingUpdateRequest(product: any): Observable<any> {
      const toSave = {
         ...product,
         opsPrice: null,
         finalComment: '',
         captainApproved: 0,
      }

      const apiCall = this.pricingClient.overridePricingUpdateRequest(toSave)

      return apiCall.pipe(
         switchMap((_) => this.pricingClient.getBatchInfo(product.practiceSKey)),
         tap(() => {
            product.opsPrice = null
            product.finalComment = ''
            product.captainApproved = 0
            product.zohoTicketId = ''
            product.isSaving = false
         }),
         catchError((error) => {
            product.isSaving = false
            if (error?.response?.indexOf('"error":"RulesEngineValidation"') === -1) {
               this.toastr.error('Server communication error. Please contact support!')
            }
            return throwError(() => error)
         })
      )
   }

   approve(product: PricingRowVM): void {
      this.updateRowVMs([{ ...product, isSaving: true }])
      const apicall: Observable<any> =
         product.changeType === 'Practice'
            ? this.pricingClient.approvePricingChangeRequest({ id: product.pricingChangeRequestId } as PricingChangeRequest)
            : this.pricingClient.approvePricingUpdateRequest(product.cycleUpdateId)

      apicall
         .pipe(
            tap(() => {
               this.updateRowVMs([{ ...product, isSaving: false, approvalStatus: 'Approved', opsPrice: product.newPrice }])
            }),
            catchError((error) => {
               product.isSaving = false
               if (error?.response?.indexOf('"error":"RulesEngineValidation"') === -1) {
                  this.toastr.error('Server communication error. Please contact support!')
               }
               return throwError(() => error)
            })
         )
         .subscribe()
   }

   reject(product: PricingRowVM, comment: string): void {
      this.updateRowVMs([{ ...product, finalComment: comment }])

      const apicall: Observable<any> =
         product.changeType === 'Practice'
            ? this.pricingClient.rejectPricingChangeRequest({
                 id: product.pricingChangeRequestId,
                 finalComment: comment,
              } as PricingChangeRequest)
            : this.pricingClient.rejectPricingUpdateRequest(product.cycleUpdateId)

      apicall
         .pipe(
            tap(() => {
               this.updateRowVMs([{ ...product, approvalStatus: 'Rejected', isSaving: false }])
            }),
            catchError((error) => {
               product.isSaving = false
               if (error?.response?.indexOf('"error":"RulesEngineValidation"') === -1) {
                  this.toastr.error('Server communication error. Please contact support!')
               }
               return throwError(() => error)
            })
         )
         .subscribe()
   }

   isValidPrice(value: any): boolean {
      return typeof value === 'number' && !isNaN(value)
   }

   validateRationale(text: string, label = 'Rationale'): boolean {
      if (!text?.trim()) {
         this.toastr.error(`${label} is required to continue`)
         return false
      }
      if (text.trim().length <= 5) {
         this.toastr.error(`Please provide a longer ${label.toLowerCase()}`)
         return false
      }
      return true
   }

   validateNewPrice(currentPrice: number, newPrice: number): boolean {
      if (isNaN(newPrice) || String(newPrice) == 'NaN' || !this.isValidPrice(newPrice)) {
         this.toastr.error('Only numeric values allowed')
         return false
      }
      if (newPrice <= 0) {
         this.toastr.error('Value must be greater than 0')
         return false
      }

      if (newPrice > currentPrice * 2.0 || newPrice < currentPrice / 2.0) {
         // Optionally return an indicator to show modal
         const message =
            newPrice > currentPrice ? 'The new price is more than double the current price.' : 'The new price is less than half the current price.'
         this.toastr.warning(message)
         return false
      }

      return true
   }

   requestNewPrice(row: PricingRowVM) {
      const currentPrice = row.currentPrice
      const newPrice = row.newPrice

      if (!this.validateNewPrice(currentPrice, newPrice)) {
         return
      }

      row.isSaving = true
      this.updateRowVMs([row])

      const apiCall = this.pricingClient.addOrUpdatePricingChangeRequest({
         id: 0,
         reviewPrice: row.newPrice,
         reviewComment: row.rationale,
         practiceSKey: row.practiceSKey,
         recordType: row.recordType,
         pimsId: row.pimsId,
         liveSystemPrice: row.currentPrice,
      } as PricingChangeRequest)

      apiCall
         .pipe(
            tap((id) => {
               row.isSaving = false
               row.isEditing = false
               row.changeType = 'Practice'
               row.approvalStatus = 'Pending'
               row.pricingChangeRequestId = id
               this.updateRowVMs([row])
            })
         )
         .subscribe()
   }

   savePricing(product: PricingRowVM, isCaptain: boolean) {
      if (product.newPrice > product.currentPrice * 2.0 || product.newPrice < product.currentPrice / 2.0) {
         // Optionally return an indicator to show modal
         const message =
            product.newPrice > product.currentPrice
               ? 'The new price is more than double the current price.'
               : 'The new price is less than half the current price.'
         this.toastr.warning(message)
      }

      product.isSaving = true

      const apiCall =
         product.changeType === 'Cycle'
            ? this.pricingClient.savePricingUpdate(product.cycleUpdateId, product.newPrice, product.rationale)
            : this.pricingClient.addOrUpdatePricingChangeRequest({
                 id: product.pricingChangeRequestId ?? 0,
                 reviewPrice: product.newPrice,
                 reviewComment: product.rationale,
                 opsPrice: product.opsPrice,
                 finalComment: product.finalComment,
                 practiceSKey: product.practiceSKey,
                 recordType: product.recordType,
                 pimsId: product.pimsId,
                 liveSystemPrice: product.currentPrice,
              } as PricingChangeRequest)

      apiCall
         .pipe(
            tap((data) => {
               product.isSaving = false
               if (product.changeType === 'Practice') {
                  product.pricingChangeRequestId = data
               }
               if (!isCaptain) {
                  product.isEditing = false
               }
            }),
            catchError((error) => {
               product.isSaving = false
               if (error?.response?.indexOf('"error":"RulesEngineValidation"') === -1) {
                  this.toastr.error('Server communication error. Please contact support!')
               }
               return throwError(() => error)
            })
         )
         .subscribe()
   }

   savePricingOps(product: PricingRowVM): void {
      const isZoho = !!product.zoho

      if (!isZoho && !this.validateNewPrice(product.currentPrice, product.opsPrice)) {
         return
      }
      if (!this.validateRationale(product.finalComment, 'Final Comment')) {
         return
      }

      product.isSaving = true

      let apiCall
      if (!product.cycleUpdateId) {
         apiCall = this.pricingClient.overridePricingUpdateRequest({
            id: product.cycleUpdateId,
            opsPrice: product.opsPrice,
            finalComment: product.finalComment,
            captainApproved: 1,
         } as PricingCycleUpdate)
      } else if (isZoho) {
         apiCall = this.pricingClient
            .createZohoTicketForChangeRequest({
               id: product.pricingChangeRequestId,
               opsPrice: product.opsPrice,
               finalComment: product.finalComment,
               actionedOn: new Date(),
            } as PricingChangeRequest)
            .pipe(
               tap((data) => {
                  product.zohoTicketId = data
               })
            )
      } else {
         apiCall = this.pricingClient.overridePricingChangeRequest({
            id: product.pricingChangeRequestId,
            opsPrice: product.opsPrice,
            finalComment: product.finalComment,
            captainApproved: 1,
            actionedOn: new Date(),
            notificationSent: null,
         } as PricingChangeRequest)
      }

      apiCall = apiCall
         .pipe(
            tap((data) => {
               product.approvalStatus = 'Approved'
               product.isEditing = false
               product.isSaving = false
            }),
            catchError((error) => {
               product.isSaving = false
               if (error?.response?.indexOf('"error":"RulesEngineValidation"') === -1) {
                  this.toastr.error('Server communication error. Please contact support!')
               }
               return throwError(() => error)
            })
         )
         .subscribe()
   }
   _showValidationModal(message: string) {
      this.showValidationModal.set(true)
      this.validationMessage.set(message)
   }

   openDetails(modalService: BsModalService, product: PricingRowVM, isPracticeChange: boolean, isCaptain: boolean): BsModalRef {
      const isEditing = isPracticeChange && product.approvalStatus === 'Pending'

      const chartOptions = {
         series: [
            { name: 'Quantity Transacted', data: [] },
            { name: 'Revenue', data: [] },
         ],
         chart: { height: 350, type: 'area' },
         xaxis: { type: 'text' },
      }

      this.selectedPrice.set(product)
      this.selectedGroup.set(this._getPriceGroup(product))

      this.updateState({
         priceChangeData: [],
         detailButtonClicked: true,
         chartOptionsTwo: chartOptions,
         isEditing: isEditing,
      })

      const modalRef = modalService.show(VetcorPricingDetailsModal, {
         class: 'modal-xl custom-modal pricing-big-modal modal-padding',
         backdrop: 'static',
         initialState: {
            isCaptain,
            isPracticeChange: isPracticeChange,
         },
      })

      setTimeout(() => {
         this.getOverrideReason(product)
         this.getPriceChangeHistory(product)
         const practiceSKeyNumber = Number(product.practiceSKey)
         this.getTransactionHistory(practiceSKeyNumber, product.pimsId)
      }, 900)

      return modalRef
   }

   updateState(updates: any) {
      this.priceChangeData.set(updates.priceChangeData)
      this.detailButtonClicked.set(updates.detailButtonClicked)
      this.chartOptionsTwo.set(updates.chartOptionsTwo)
      this.isEditing.set(updates.isEditing)
   }

   getOverrideReason(product): void {
      if (!product) {
         this.recOverrideReason.set('N/A')
         return
      }
      if (!!product.reviewPrice && product.reviewPrice !== 0) {
         this.recOverrideReason.set('N/A')
         return
      }

      const reviewPrice = product.reviewPrice || product.newPrice
      if (reviewPrice <= product.lowballPriceFloor && product.lowballReasonCode > 0) {
         this.pricingClient.getLowballReason(product.lowballReasonCode).subscribe(
            (data) => {
               this.recOverrideReason.set(data || 'N/A')
            },
            (error) => {
               console.error('Error fetching lowball reason:', error)
               this.recOverrideReason.set('N/A')
            }
         )
      } else if (reviewPrice > product.lowballPriceFloor) {
         if (reviewPrice > product.modelPrice) {
            this.recOverrideReason.set('Approve requested increase if clinic explains why they feel they can stretch further than model.')
         } else if (reviewPrice < product.modelPrice && reviewPrice >= 1.05 * product.averageUnitRetail) {
            this.recOverrideReason.set('New price is at least 5% higher than historical.')
         }
      } else if (reviewPrice < product.modelPrice && reviewPrice < 1.05 * product.averageUnitRetail) {
         this.recOverrideReason.set('Requested price does not offset expected annual input cost inflation.')
      } else {
         this.recOverrideReason.set('N/A')
      }
   }

   getPriceChangeHistory(product): void {
      this.pricingClient
         .getPriceChangeHistory({ practiceSKey: product.practiceSKey, pimsId: product.pimsId, recordType: product.recordType } as any)
         .subscribe({
            next: (data) => {
               this.priceChangeData.set(data)
            },
            error: (error) => {
               console.error('Error fetching price change history:', error)
            },
         })
   }

   getTransactionHistory(practiceSKey: number, pimsId: string): void {
      if (!!practiceSKey && !!pimsId) {
         const practiceSKeyNumber = Number(practiceSKey)

         this.pricingClient
            .getProductTransactionHistory({
               practiceSKey: practiceSKey,
               pimsId: pimsId,
            })
            .subscribe((data) => {
               const filteredData = data.filter((item) => +item.year === +this.selectedChartYear)

               // Group by month
               const monthlyData = Array(12).fill({ qty: 0, revenue: 0 })
               filteredData.forEach((item) => {
                  const monthIndex = item.month - 1 // Convert month to 0-based index
                  monthlyData[monthIndex] = {
                     qty: (monthlyData[monthIndex].qty || 0) + item.qty,
                     revenue: (monthlyData[monthIndex].revenue || 0) + item.revenue,
                  }
               })

               // Prepare chart series data
               const quantities = monthlyData.map((item) => item.qty || 0)
               const revenues = monthlyData.map((item) => item.revenue || 0)

               this.chartOptionsTwo.set({
                  series: [
                     {
                        name: 'Quantity Transacted',
                        data: quantities,
                     },
                     {
                        name: 'Revenue',
                        data: revenues,
                     },
                  ],
                  chart: {
                     height: 350,
                     type: 'area',
                  },
                  stroke: {
                     curve: 'smooth',
                  },
                  dataLabels: {
                     enabled: true,
                     formatter: function (val: number, opts: any) {
                        if (opts.seriesIndex === 1) {
                           return `$${val.toFixed(0)}`
                        }
                        return `${val}`
                     },
                  },
                  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                  xaxis: {
                     type: 'text',
                  },
                  yaxis: [
                     {
                        title: {
                           text: 'Quantity Transacted',
                        },
                     },
                     {
                        opposite: true,
                        title: {
                           text: 'Revenue',
                        },
                        labels: {
                           formatter: function (val: number) {
                              return `$${val.toFixed(0)}`
                           },
                        },
                     },
                  ],
               })
            })
      }
   }

   toggleEditing(group: PricingRowVM) {
      const newIsEditing = group.isEditing === undefined ? true : !group.isEditing
      this.updateRowVMs([
         {
            ...group,
            isEditing: newIsEditing,
         },
      ])
   }

   toggleExpand(group: PricingRowVM) {
      group.isExpanded = !group.isExpanded
      this.updateRowVMs([group])
   }

   updateRowVMs(rows: PricingRowVM[]) {
      const prices = [...this.topLevelRows()]

      rows.forEach((row) => {
         let topLevelIndex

         if (!!row.parentId) {
            topLevelIndex = prices.findIndex((g) => g.id === row.parentId)

            // Update child in the parent
            const childToUpdateIndex = prices[topLevelIndex].subRows?.findIndex((sr) => sr.id === row.id)
            const newSubRows = [...prices[topLevelIndex].subRows]
            newSubRows[childToUpdateIndex] = row
            prices[topLevelIndex] = {
               ...prices[topLevelIndex],
               subRows: newSubRows,
            }
         }

         topLevelIndex = prices.findIndex((g) => g.id === row.id)
         prices[topLevelIndex] = row
      })

      this.topLevelRows.set(prices)
   }

   saveNewCodeRequest(ncr: NewCodeRequest, callback: () => void) {
      this.updateRowVMs([{ ...this.selectedPrice(), isSaving: true }])

      const newRow: PricingRowVM = {
         ...this.selectedPrice(),
         isSaving: true,
         approvalStatus: ncr.isApproved ? 'Approved' : ncr.isApproved === false ? 'Rejected' : 'Pending',
         opsPrice: ncr.productPrice,
      }

      this._applyVMPriceCalculations(newRow)

      this.pricingClient
         .saveNewCodeRequest(ncr)
         .pipe(
            tap(() => {
               this.updateRowVMs([
                  {
                     ...newRow,
                     isSaving: false,
                  },
               ])
               callback()
            })
         )
         .subscribe()
   }

   /*
   getTotalPrice(group: any, type: 'default' | 'ops'): number {
      const price = type === 'ops' ? group.product.opsPrice ?? group.product.modelPrice : group.product.newPrice ?? group.product.modelPrice

      const feeRows = this.vmRowVal.calculateTotalRequestedFee(group.subFeeRows)
      const systemRows = this.vmRowVal.calculateSystemRequestedFee(group.subSystemRows)

      return price + feeRows + systemRows
   }
      */
}
